import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { clamp, roundTo } from '../types/geometry.js';
import { analyzeBackgroundSurface, channelSpread, clampRect } from '../quality/regions.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * «Качество фона».
 *
 * Independent of the existing `background` check (which keeps clutter/objects
 * and the document's target colour): here the question is how *usable* the
 * background is as a plain backdrop — uniformity, brightness and neutrality,
 * the overall gradient, and a dark halo right at the subject boundary.
 *
 * Never a hard failure: an uneven or dark background is a retake reason.
 */
export const backgroundQualityCheck: ValidationCheck = {
  id: 'background_quality',
  title: 'Качество фона',
  description:
    'Оценивает однородность, светлоту и нейтральность фона, перепад яркости и тёмный контур у границы человека.',
  kind: 'background',
  requires: ['background', 'pixels'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['background', 'pixels']);
    if (missing) return notChecked(ctx, backgroundQualityCheck, missing, ctx.rule.severity);
    const background = ctx.background;
    const pixels = ctx.pixels;
    if (!background || !pixels) {
      return notChecked(
        ctx,
        backgroundQualityCheck,
        'Недостаточно данных для оценки качества фона.',
        ctx.rule.severity,
      );
    }

    const minUniformity = numberParam(ctx.rule.params, 'minUniformity', 0.7);
    /**
     * The profile may express the allowed gradient as a fraction of the luma
     * scale (0,12) or directly in luma units (30). Both are honoured.
     */
    const rawMaxGradient = numberParam(ctx.rule.params, 'maxGradient', 0.18);
    const maxGradientLuma = rawMaxGradient <= 1 ? rawMaxGradient * 255 : rawMaxGradient;
    const minMeanLuma = numberParam(ctx.rule.params, 'minMeanLuma', 235);
    const maxTargetDeviation = numberParam(ctx.rule.params, 'maxTargetDeviation', 0.12);
    /** A neutral background has no channel spread; a warm cast is not "white". */
    const maxChannelSpread = numberParam(ctx.rule.params, 'maxChannelSpread', 20);
    const maxHaloRatio = numberParam(ctx.rule.params, 'maxHaloRatio', 0.4);
    const uncertainBelowConfidence = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.35);
    const minBackgroundCells = numberParam(ctx.rule.params, 'minBackgroundCells', 6);

    const surface = analyzeBackgroundSurface({
      buffer: pixels,
      subjectRect: background.subjectRect,
      ...(ctx.geometry ? { geometry: ctx.geometry } : {}),
    });

    const spread = channelSpread(background.meanColor);
    const gradient = Math.max(
      Math.abs(surface.horizontalGradient),
      Math.abs(surface.verticalGradient),
    );

    const coverageScore = clamp(background.maskCoverage / 0.25, 0, 1);
    const cellScore = clamp(surface.cells.length / 16, 0, 1);
    const confidence = clamp(0.3 + coverageScore * 0.5 + cellScore * 0.2, 0, 1);

    const notEnoughBackground =
      !background.hasMask ||
      surface.backgroundPixels < 128 ||
      surface.cells.length < minBackgroundCells ||
      confidence < uncertainBelowConfidence;

    if (notEnoughBackground) {
      return plainResult({
        id: backgroundQualityCheck.id,
        title: backgroundQualityCheck.title,
        status: 'uncertain',
        severity: ctx.rule.severity,
        message:
          '⚠ Фон вокруг головы почти не виден, поэтому оценить его качество не удалось.',
        requirement: 'Фон должен быть однородным и светлым.',
        fixability: 'retake',
        confidence: roundTo(confidence, 2),
        details: {
          uniformity: background.uniformity,
          meanLuma: roundTo(background.meanLuma, 1),
          meanColor: background.meanColor,
          channelSpread: roundTo(spread, 1),
          targetDeviation: background.targetDeviation,
          horizontalGradient: roundTo(surface.horizontalGradient, 1),
          verticalGradient: roundTo(surface.verticalGradient, 1),
          gradient: roundTo(gradient, 1),
          haloRatio: roundTo(surface.haloRatio, 4),
          maskCoverage: background.maskCoverage,
          backgroundCells: surface.cells.length,
          limits: {
            minUniformity,
            maxGradient: roundTo(maxGradientLuma, 1),
            minMeanLuma,
            maxTargetDeviation,
            maxChannelSpread,
            maxHaloRatio,
          },
          reason: 'not_enough_background',
        },
      });
    }

    const nonUniform = background.uniformity < minUniformity || gradient > maxGradientLuma;
    const tooDark =
      background.meanLuma < minMeanLuma ||
      background.targetDeviation > maxTargetDeviation ||
      spread > maxChannelSpread;
    const halo = surface.haloRatio > maxHaloRatio;

    const status: CheckResult['status'] = nonUniform || tooDark || halo ? 'warning' : 'pass';
    const message =
      status === 'pass'
        ? '✅ Фон однородный и светлый'
        : tooDark
          ? '⚠ Фон темнее требуемого'
          : '⚠ Фон неравномерный';

    /**
     * A halo is a thin band around the subject, so the marker is that band
     * rather than the whole frame: the overlay should point at the contour.
     */
    const haloBand = clampRect({
      x: background.subjectRect.x - 0.04,
      y: background.subjectRect.y - 0.04,
      width: background.subjectRect.width + 0.08,
      height: background.subjectRect.height + 0.08,
    });
    const problemRegions =
      status === 'warning' && halo
        ? [
            {
              id: 'background_quality_halo',
              rect: {
                x: roundTo(haloBand.x, 4),
                y: roundTo(haloBand.y, 4),
                width: roundTo(haloBand.width, 4),
                height: roundTo(haloBand.height, 4),
              },
              message: 'Вдоль границы человека фон темнее — виден тёмный контур.',
              severity: 'warning' as const,
            },
          ]
        : [];

    return plainResult({
      id: backgroundQualityCheck.id,
      title: backgroundQualityCheck.title,
      status,
      severity: ctx.rule.severity,
      value: roundTo(background.uniformity, 3),
      min: minUniformity,
      unit: 'ratio',
      message,
      requirement:
        'Фон должен быть однородным, светлым и нейтральным, без перепадов яркости и тёмного контура у человека.',
      fixability: 'retake',
      geometry: [
        {
          type: 'rect',
          coordinates: [
            roundTo(background.subjectRect.x, 4),
            roundTo(background.subjectRect.y, 4),
            roundTo(background.subjectRect.width, 4),
            roundTo(background.subjectRect.height, 4),
          ],
        },
      ],
      problemRegions,
      confidence: roundTo(confidence, 2),
      details: {
        uniformity: background.uniformity,
        meanLuma: roundTo(background.meanLuma, 1),
        meanColor: background.meanColor,
        channelSpread: roundTo(spread, 1),
        targetDeviation: background.targetDeviation,
        horizontalGradient: roundTo(surface.horizontalGradient, 1),
        verticalGradient: roundTo(surface.verticalGradient, 1),
        gradient: roundTo(gradient, 1),
        gradientScore: roundTo(gradient / 255, 4),
        haloRatio: roundTo(surface.haloRatio, 4),
        haloPixels: surface.haloPixels,
        haloMeanLuma: roundTo(surface.haloMeanLuma, 1),
        backgroundCells: surface.cells.length,
        backgroundPixels: surface.backgroundPixels,
        maskCoverage: background.maskCoverage,
        cornerLumaSpread: background.cornerLumaSpread,
        limits: {
          minUniformity,
          maxGradient: roundTo(maxGradientLuma, 1),
          minMeanLuma,
          maxTargetDeviation,
          maxChannelSpread,
          maxHaloRatio,
        },
        reasons: {
          nonUniform,
          tooDark,
          halo,
        },
        displayValue:
          status === 'pass'
            ? 'однородный'
            : tooDark
              ? 'темнее требуемого'
              : 'неравномерный',
      },
    });
  },
};
